import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Lightning, HandHeart, Users } from "@phosphor-icons/react";
import Seo from "../components/Seo";

const PrincipleCard = ({ icon: Icon, title, body }) => (
  <div className="hard-border bg-cyber-surface p-6 sm:p-8" data-testid={`principle-${title.toLowerCase().replace(/\s+/g,'-')}`}>
    <Icon size={24} weight="duotone" className="text-cyber-green" />
    <h3 className="font-display font-black text-2xl mt-4">{title}</h3>
    <p className="text-white/65 mt-3 leading-relaxed text-sm">{body}</p>
  </div>
);

const About = () => {
  return (
    <main className="grain" data-testid="about-page">
      <Seo
        path="/about"
        title="About FintechOmni"
        description="Built for fintech marketing pros. No stage, no speakers, no panels — just decision-makers in the room. Sub-20% approval rate, every conversation off the record."
        keywords="about fintechomni, fintech marketing community, private fintech events, fintech operators"
      />
      {/* HERO */}
      <section className="border-b border-cyber-line">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-12 sm:pt-20 pb-12 sm:pb-16">
          <div className="term-label mb-4 sm:mb-5">/ about</div>
          <h1 className="font-display font-black tracking-tighter text-[44px] sm:text-7xl lg:text-[100px] leading-[0.95] sm:leading-[0.92] max-w-5xl">
            Built for fintech <br />
            <span className="text-white">marketing pros.</span>
          </h1>
          <p className="mt-6 sm:mt-8 text-white/75 text-base sm:text-xl max-w-3xl leading-relaxed">
            No stage. No speakers. No panels-from-LinkedIn. Just decision-makers in the room — matched by hand, sub-20% approval rate, every conversation off the record.
          </p>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="border-b border-cyber-line">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <div className="term-label mb-2">/ who it's for</div>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl leading-tight">The room.</h2>
          </div>
          <div className="lg:col-span-8 space-y-5 text-white/80 text-lg leading-relaxed">
            <p>
              Heads of growth, performance marketing leads, paid media operators, CMOs, brand directors, and creative teams at category-defining fintechs.
            </p>
            <p>
              Every RSVP is reviewed by hand. No VC-panel tourists. No agency pitch-tourists. No press without credentials. The room is the product.
            </p>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="border-b border-cyber-line bg-cyber-black">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
            <div className="lg:col-span-4">
              <div className="term-label mb-2">/ how we operate</div>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl leading-tight">Four principles.</h2>
            </div>
            <div className="lg:col-span-8 text-white/65 text-lg">
              These aren't values on a wall. They're the rules every guest list, floor plan, and approval gets pressure-tested against.
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <PrincipleCard
              icon={Users}
              title="No stage. No speakers."
              body="Nobody's pitching from a podium. No keynote slides, no moderator monologues, no panel-of-LinkedIn. The room is the content — operators in conversation with operators."
            />
            <PrincipleCard
              icon={Target}
              title="Decision-makers in the room"
              body="Heads of growth, CMOs, paid-media leads, founders, and budget-holders. The people who can actually green-light a partnership are the ones standing next to you at the bar."
            />
            <PrincipleCard
              icon={HandHeart}
              title="Sub-20% approval rate"
              body="Every RSVP is reviewed by hand. Fewer than one in five gets in. That's not a flex — it's the only way to keep the signal high and the noise out."
            />
            <PrincipleCard
              icon={Lightning}
              title="Matched, not crowded"
              body="The questions you answer at sign-up aren't admin. We use them to put you next to the people you'd most want to meet — by stack, stage, segment, and what you're actually trying to ship next quarter."
            />
          </div>
        </div>
      </section>

      {/* WHY IT EXISTS */}
      <section className="border-b border-cyber-line">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <div className="term-label mb-2">/ why it exists</div>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl leading-tight">The gap.</h2>
          </div>
          <div className="lg:col-span-8 space-y-5 text-white/80 text-lg leading-relaxed">
            <p>
              We kept attending fintech events built for everyone — and useful to no-one. The content was generic, the rooms were oversold, and the follow-ups never came.
            </p>
            <p>
              FintechOmni is the event we wanted to attend ourselves. Built by performance marketers, for performance marketers. A brand that treats the attendee as the customer, and the sponsor as a partner — not the other way around.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white text-cyber-black">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 sm:py-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <div className="font-mono-tech text-xs uppercase tracking-[0.2em]">/ next</div>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-2 leading-none">Come to the next one.</h2>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Link to="/events" className="bg-cyber-black text-white border border-cyber-black hover:bg-cyber-black/85 px-7 py-4 font-mono-tech tracking-[0.12em] uppercase font-bold text-sm transition-colors" data-testid="about-cta-events">
              See upcoming events <ArrowRight size={14} weight="bold" className="inline ml-2" />
            </Link>
            <Link to="/contact" className="border border-cyber-black text-cyber-black hover:bg-cyber-black hover:text-white px-7 py-4 font-mono-tech tracking-[0.12em] uppercase font-bold text-sm transition-colors" data-testid="about-cta-contact">
              Talk to us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
